// IEA Site Survey page, step 2 shell. Admin only, behind the ieaWalk switch.
// Words: IEA_OFFLINE_SHELL_WORDS.md rev 3 (approved 2026-10-14). Mockup: MOCKUP_IEA_SHELL.html.
// Addresses:
//   #/survey                      site list
//   #/survey/{site}               opens the Map, or the site page when the check blocks (IEA_SURVEY_ENTRY_WORDS.md)
//   #/survey/{site}/walk          the one site page: counts, Open Map, Start here, site check
//   #/survey/{site}/a/{assetId}   one asset, opened by a scan or a tap
//   #/survey/{site}/outbox        the Outbox
//   #/survey/{site}/map           Map, pass 1 (survey-map-page.ts)
//   #/survey/{site}/ids           Asset IDs: the only place that makes an asset (asset-id-page.ts)
//   #/survey/{site}/ids/{room}    Add Asset from the Map, the room filled in
// Works with no signal once a site pack is on the device. Saves wait on the device until the cloud
// confirms them (survey-sync.ts).

import { keepSearchFocus, searchFocus, wireSitePicker } from "./site-picker";
import "./survey.css";
import type { SignInClaims } from "./badge";
import { loadDriveSettings, loadSurveySites, readSitePack, readWalkStatus, type SurveySite } from "./firebase";
import { driveNotConnectedLine, drivePill, siteHasFolder } from "./drive-screens";
import { driveReadDue } from "./drive-folders";
import { deviceList, isDrivePdf, otherDeviceFrom, outboxCounts, type LogLine, type WalkStatusDoc } from "./outbox";
import { deviceId, exportBackup, loadBackup, markSurvey, onOutboxChange, outboxNow, photosHeld, sendWaiting, setPhotosHeld } from "./survey-sync";
import { isOnline } from "./offline";
import { startQrScanner, type QrScanner } from "./qr-scanner";
import { cleanDeviceName, defaultLarge, isUtility, mapWarnLine, preCheck, resolveScan, surveyCounts, tracedSet, type CheckInput, type CheckResult, type SitePack } from "./survey";
import { assetSummary, creatorSiteList, nameSheet, needsSwitch, outboxScreen, packDownloading, scanResult, scanSheet, siteList, sitePage, strip, trail, type OutboxView } from "./survey-screens";
import { errorScreen, esc, loadingScreen } from "./training-screens";
import { drawMap, mapAssetsChanged, mapScanned } from "./survey-map-page";
import { drawAssetIds, overlayLocalAssets } from "./asset-id-page";
import { drawFieldLabels } from "./field-labels-page";

export interface SurveyHost {
  root: HTMLElement;
  badge: SignInClaims;
  /** Draws the shared header, then `above` full width, then `inner` in the page column. */
  frame: (above: string, inner: string) => void;
}

interface PackMeta {
  savedAt: string;
  counts: { assets: number; iea: number; links: number };
}

let host: SurveyHost | null = null;
let sites: SurveySite[] | null = null;
/** settings/drive for the Creator's site list (SITE_FOLDERS_WORDS.md panel A). Null: never set up or not read. */
let driveSettings: { connected: boolean; rootFolderId: string } | null = null;
let driveReadAt = 0;

/** Reads the Drive connection again when the last read is old, so Connect Drive shows without closing the app. */
async function refreshDrive(): Promise<void> {
  if (!host || !host.badge.authorities.includes("admin") || !isOnline() || !driveReadDue(driveReadAt, Date.now())) return;
  driveReadAt = Date.now();
  const d = await loadDriveSettings(host.badge.company);
  const next = d ? { connected: d.connected === true, rootFolderId: String(d.rootFolderId ?? "") } : null;
  if (JSON.stringify(next) === JSON.stringify(driveSettings)) return;
  driveSettings = next;
  rerender();
}
let sitesError = "";
const packs = new Map<string, SitePack>();
let packBusy = "";
let packError = "";
let scanOpen = false;
let scanNote = "";
let scanner: QrScanner | null = null;
/** The video the camera runs in, and its last status line. A repaint moves both into the new sheet. */
let scanVideo: HTMLVideoElement | null = null;
let scanText = "";
let nameOpen = false;
let wired = false;
/** Items not yet sent, for the strip and the check. Refreshed on every Outbox change. */
let waitingNow = 0;
/** Saves the cloud refused: the Outbox warning shows for these even online. */
let refusedNow = 0;
let outboxView: { records: Awaited<ReturnType<typeof outboxNow>>["records"]; photos: Awaited<ReturnType<typeof outboxNow>>["photos"]; log: LogLine[] } | null = null;
let storageNow: OutboxView["storage"] = { usedMb: null, freeMb: null, kept: null };
let obBusy = "";
let obFlash = "";
let obError = "";
const walkStatus = new Map<string, WalkStatusDoc | null>();
/** Sites whose pack this visit already tried to download on the way to the Map. One try, then the site page. */
const autoTried = new Set<string>();
/** The site whose Map is open. Opening it marks the survey Open once, not on every repaint. */
let mapOpenFor = "";

// ---- This device (wrapped: storage can be missing or refuse) ----

function store(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
function getItem(k: string): string | null {
  try {
    return store()?.getItem(k) ?? null;
  } catch {
    return null;
  }
}
function setItem(k: string, v: string): void {
  try {
    store()?.setItem(k, v);
  } catch {
    // Storage full or blocked: the survey still runs, the setting is not remembered.
  }
}

const DEVICE_KEY = "st_survey_device";
const AA_KEY = "st_survey_aa";
const packKey = (company: string, site: string) => `st_survey_pack_${company}_${site}`;

export function deviceName(): string {
  return getItem(DEVICE_KEY) ?? "";
}

function isLarge(): boolean {
  const v = getItem(AA_KEY);
  if (v === "large") return true;
  if (v === "standard") return false;
  return defaultLarge(navigator.maxTouchPoints ?? 0, Math.min(screen.width, screen.height));
}

function packMeta(site: string): PackMeta | null {
  if (!host) return null;
  const raw = getItem(packKey(host.badge.company, site));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PackMeta;
  } catch {
    return null;
  }
}

function homeScreenApp(): boolean {
  return window.matchMedia?.("(display-mode: standalone)").matches === true || (navigator as { standalone?: boolean }).standalone === true;
}

// ---- Routing ----

function parts(): string[] {
  return location.hash.replace(/^#\/?/, "").split("/").filter(Boolean).map(decodeURIComponent);
}

/** The survey and the Asset ID Creator share this module: same site pack, Outbox and offline shell. */
function inSection(): boolean {
  const s = parts()[0];
  return s === "survey" || s === "assets";
}

/** Add Site made a new site: read the site list again on the next open (SITE_GROWTH_FIXES_WORDS.md item 1). */
export function dropSurveySites(): void {
  sites = null;
  sitesError = "";
}

function siteOf(n: string): SurveySite | null {
  return sites?.find((s) => s.siteNumber === n) ?? null;
}

export function renderSurvey(h: SurveyHost): void {
  host = h;
  wire();
  h.root.classList.toggle("sv-large", isLarge());
  h.root.classList.toggle("ac-wide", parts()[0] === "assets");
  if (parts()[0] === "assets") return renderCreator(h);
  if (!h.badge.authorities.includes("ieaWalk")) {
    h.frame(trail([{ label: "IEA Site Survey" }], false), needsSwitch());
    return;
  }
  if (sites === null) {
    h.frame(trail([{ label: "IEA Site Survey" }], false), sitesError ? sitesErrorScreen() : loadingScreen("sites"));
    if (!sitesError) void loadSites();
    return;
  }
  const p = parts();
  const site = p[1] ? siteOf(p[1]) : null;
  if (!p[1]) return drawSites();
  if (!site) return h.frame(trail([{ label: "IEA Site Survey", href: "#/survey" }, { label: p[1] }], false), errorScreen("That site is not one your badge covers."));
  if (!packs.has(site.siteNumber) && packMeta(site.siteNumber)) {
    h.frame(trail([{ label: "IEA Site Survey", href: "#/survey" }, { label: site.name }], false), loadingScreen("the site pack"));
    void openPack(site);
    return;
  }
  if (p[2] !== "map") mapOpenFor = "";
  const pack = packs.get(site.siteNumber);
  if (!p[2]) {
    // Tap the site card and the Map opens. The check stops you only when it has to.
    if (!pack && isOnline() && !packError && !autoTried.has(site.siteNumber)) {
      autoTried.add(site.siteNumber);
      void downloadPack(site);
    }
    if (!pack && packBusy === "pack") return drawDownloading(site);
    if (!pack || checkFor(site).result.blocks.length) return drawSite(site);
    location.replace(`#/survey/${site.siteNumber}/map`);
    return;
  }
  if (p[2] === "walk") return drawSite(site);
  if (!pack) {
    location.replace(`#/survey/${site.siteNumber}`);
    return;
  }
  if (p[2] === "outbox") return drawOutbox(pack);
  if (p[2] === "map") {
    if (mapOpenFor !== site.siteNumber) {
      mapOpenFor = site.siteNumber;
      startSurvey(site.siteNumber);
    }
    if (!deviceName()) nameOpen = true;
    loadStatusOnce(site.siteNumber);
    drawMap(
      {
        root: h.root,
        badge: h.badge,
        // The scan and name sheets ride every Map repaint, so the Map's own repaints never drop them
        // (NAV_CRAWL_WORDS.md A7). The running camera goes back into the new sheet.
        frame: (above, inner) => {
          h.frame(above, inner + overlays());
          keepScanner();
        },
        deviceName,
        online: isOnline,
        waiting: () => waitingNow,
        large: isLarge,
        warn: () => mapWarnLine(checkFor(site).input),
        packBusy: () => packBusy === "pack",
      },
      pack,
    );
    return;
  }
  if (p[2] === "ids") {
    // The Creator has its own section now (ASSET_IDS_SECTION_WORDS.md section 3).
    location.replace(`#/assets/${site.siteNumber}${p[3] ? `/${encodeURIComponent(p[3])}` : ""}`);
    return;
  }
  if (p[2] === "a" && p[3]) return drawAsset(pack, p[3]);
  location.replace(`#/survey/${site.siteNumber}/walk`);
}

// ---- Asset ID Creator section (#/assets): ASSET_IDS_SECTION_WORDS.md rev 3.2, approved 2026-10-21 ----
// Admin only. No IEA Site Survey switch needed. Same site pack, Outbox and offline shell as the survey.

function creatorTrail(siteName?: string): string {
  return trail(siteName ? [{ label: "Assessment Sheet", href: "#/assets" }, { label: siteName }] : [{ label: "Assessment Sheet" }], false);
}

function renderCreator(h: SurveyHost): void {
  if (!h.badge.authorities.includes("admin")) {
    h.frame(creatorTrail(), '<div class="note red">The Assessment Sheet needs an admin badge.</div>');
    return;
  }
  if (sites === null) {
    h.frame(creatorTrail(), sitesError ? sitesErrorScreen() : loadingScreen("sites"));
    if (!sitesError) void loadSites();
    return;
  }
  const p = parts();
  if (!p[1]) void refreshDrive();
  if (!p[1]) {
    const list = sites.map((s) => {
      const m = packMeta(s.siteNumber);
      const prefix = `companies/${h.badge.company}/sites/${s.siteNumber}/assets/`;
      const waiting = (outboxView?.records ?? []).filter((r) => r.path.startsWith(prefix) && r.state !== "sent").length;
      const view = { settings: driveSettings };
      const has = siteHasFolder(view, s.driveFolder);
      return { siteNumber: s.siteNumber, name: s.name, assetCount: m?.counts.assets ?? null, packSavedAt: m?.savedAt ?? null, waiting, drivePill: drivePill(view, has), hasFolder: driveSettings?.connected ? has : null };
    });
    const f = searchFocus(h.root);
    h.frame(creatorTrail(), creatorSiteList(list, driveNotConnectedLine({ settings: driveSettings }, h.badge.sites.includes("ALL"))) + overlays());
    wireSitePicker(h.root);
    keepSearchFocus(h.root, f.wasTyping, f.caret);
    return;
  }
  const site = siteOf(p[1]);
  if (!site) return h.frame(creatorTrail(p[1]), errorScreen("That site is not one your badge covers."));
  if (p[2] === "labels") {
    // Field Labels (FIELD_LABELS_WORDS.md): Step 0, the yellow labels before the Asset IDs.
    // No site pack needed: the notepad lives on this iPad and nothing here makes an asset.
    drawFieldLabels({ root: h.root, badge: h.badge, frame: h.frame, siteNumber: site.siteNumber, siteName: site.name, pack: packs.get(site.siteNumber) ?? null });
    return;
  }
  if (!packs.has(site.siteNumber) && packMeta(site.siteNumber)) {
    h.frame(creatorTrail(site.name), loadingScreen("the site pack"));
    void openPack(site);
    return;
  }
  const pack = packs.get(site.siteNumber);
  // The Outbox belongs to the Creator too. No IEA Site Survey switch needed here (COHESION_WORDS.md Phase A).
  if (pack && p[2] === "outbox") return drawOutbox(pack);
  if (!pack) {
    if (isOnline() && !packError && !autoTried.has(`ids:${site.siteNumber}`)) {
      autoTried.add(`ids:${site.siteNumber}`);
      void downloadPack(site);
    }
    if (packBusy === "pack") return h.frame(creatorTrail(site.name), packDownloading(site.name) + overlays());
    h.frame(
      creatorTrail(site.name) + stripNow(),
      `${packError ? `<div class="note red">${esc(packError)}</div>` : ""}<div class="note">This site's pack is not on this iPad yet. Download it once with signal, then the Creator works with no signal.</div>
      <div class="sv-btns"><button class="sv-btn gold" type="button" data-sv="pack"${isOnline() ? "" : " disabled"}>Download Site Pack</button><a class="sv-btn line" href="#/assets/${esc(site.siteNumber)}/labels">Field Labels</a></div>`,
    );
    return;
  }
  drawAssetIds(
    {
      root: h.root,
      badge: h.badge,
      // Same as the Map: the Creator's own repaints keep the scan sheet and its camera (NAV_CRAWL_WORDS.md A7).
      frame: (above, inner) => {
        h.frame(above, inner + overlays());
        keepScanner();
      },
      siteName: site.name,
      assetsChanged: mapAssetsChanged,
      deviceName,
      canMap: h.badge.authorities.includes("ieaWalk"),
      refreshPack: () => void downloadPack(site),
      packBusy: () => packBusy === "pack",
    },
    pack,
    p[2] === "row" || p[2] === "push" ? null : (p[2] ?? null),
    // Change In Creator from the asset page: this row picked (ONE_ASSET_PAGE_WORDS.md).
    p[2] === "row" && p[3] ? decodeURIComponent(p[3]) : null,
    // Push ER1 from Field Labels (PUSH_BY_ROOM_B7_WORDS.md item 1): the push sheet opens over the sheet, that room only.
    p[2] === "push" ? (p[3] ? decodeURIComponent(p[3]) : "") : null,
  );
}

/** The sites could not be read: say why, and offer Try Again (NAV_CRAWL_WORDS.md A4). */
function sitesErrorScreen(): string {
  return `<div class="note red">${esc(sitesError)} <button class="chg" type="button" data-sv="sites-retry">Try Again</button></div>`;
}

/** Read the sites again: Try Again, or signal coming back. */
function retrySites(): void {
  if (sites !== null || !sitesError) return;
  sitesError = "";
  rerender();
}

async function loadSites(): Promise<void> {
  if (!host) return;
  try {
    sites = await loadSurveySites(host.badge.company, host.badge.sites);
    if (host.badge.authorities.includes("admin")) {
      driveReadAt = Date.now();
      const d = await loadDriveSettings(host.badge.company);
      driveSettings = d ? { connected: d.connected === true, rootFolderId: String(d.rootFolderId ?? "") } : null;
    }
  } catch (e) {
    sitesError = isOnline() ? `Could not read the sites: ${e instanceof Error ? e.message : String(e)}` : "No signal and no sites on this device yet. Open the survey once with signal.";
  }
  rerender();
}

async function openPack(site: SurveySite): Promise<void> {
  if (!host) return;
  try {
    const meta = packMeta(site.siteNumber);
    const pack = await readSitePack(host.badge.company, site, false);
    pack.savedAt = meta?.savedAt ?? "";
    await overlayLocalAssets(pack, host.badge.company);
    packs.set(site.siteNumber, pack);
  } catch {
    // The device copy is gone (cleared by the browser). Treat it as no pack.
    setItem(packKey(host.badge.company, site.siteNumber), "");
    store()?.removeItem(packKey(host.badge.company, site.siteNumber));
  }
  rerender();
}

async function downloadPack(site: SurveySite): Promise<void> {
  if (!host || packBusy) return;
  packBusy = "pack";
  packError = "";
  rerender();
  try {
    // Ask the iPad to keep the survey's storage. The Outbox shows the answer.
    await navigator.storage?.persist?.().catch(() => false);
    const pack = await readSitePack(host.badge.company, site, true);
    pack.savedAt = new Date().toISOString();
    // New Asset IDs still waiting on this iPad stay in the pack until the cloud takes them.
    await overlayLocalAssets(pack, host.badge.company);
    packs.set(site.siteNumber, pack);
    const meta: PackMeta = { savedAt: pack.savedAt, counts: { assets: pack.assets.length, iea: pack.ieaDone.length, links: pack.links.length } };
    setItem(packKey(host.badge.company, site.siteNumber), JSON.stringify(meta));
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    packError = /permission/i.test(msg) ? "The cloud refused the site pack. Check the IEA Site Survey switch on your badge." : `Could not download the site pack: ${msg}`;
  }
  packBusy = "";
  rerender();
}

function rerender(): void {
  if (host && inSection()) renderSurvey(host);
  keepScanner();
}

// ---- Screens ----

function stripNow(): string {
  return strip({ online: isOnline(), waiting: waitingNow });
}

async function refreshOutbox(): Promise<void> {
  const { records, photos } = await outboxNow();
  const counts = outboxCounts(records, photos, new Date().toISOString());
  waitingNow = counts.waiting;
  refusedNow = counts.refused;
  let log: LogLine[] = [];
  try {
    log = await deviceList.log();
  } catch {
    log = [];
  }
  outboxView = { records, photos, log };
  try {
    const est = await navigator.storage?.estimate?.();
    const kept = (await navigator.storage?.persisted?.()) ?? null;
    storageNow = {
      usedMb: est?.usage !== undefined ? est.usage / 1048576 : null,
      freeMb: est?.quota !== undefined && est.usage !== undefined ? (est.quota - est.usage) / 1048576 : null,
      kept,
    };
  } catch {
    // Storage figures are a nice-to-have.
  }
  // Survey in progress follows the waiting count while this device has the site open.
  const site = parts()[1];
  if (host && site && getItem(startedKey(site)) && isOnline()) markSurvey(host.badge, site, deviceName(), "Open", waitingNow, getItem(startedKey(site)) ?? "");
  rerender();
}

const startedKey = (site: string) => `st_survey_started_${site}`;

async function loadStatus(site: string): Promise<void> {
  if (!host) return;
  walkStatus.set(site, await readWalkStatus(host.badge.company, site));
  rerender();
}

function drawSites(): void {
  if (!host || !sites) return;
  const list = sites.map((s) => {
    const m = packMeta(s.siteNumber);
    return { siteNumber: s.siteNumber, name: s.name, assetCount: m?.counts.assets ?? null, packSavedAt: m?.savedAt ?? null };
  });
  const f = searchFocus(host.root);
  host.frame(trail([{ label: "IEA Site Survey" }], false), siteList(list) + overlays());
  wireSitePicker(host.root);
  keepSearchFocus(host.root, f.wasTyping, f.caret);
}

/** Opening the Map does what Start Survey did: stamp the start time and mark the survey Open for other devices. */
function startSurvey(siteNumber: string): void {
  if (!host) return;
  const started = getItem(startedKey(siteNumber)) || new Date().toISOString();
  setItem(startedKey(siteNumber), started);
  markSurvey(host.badge, siteNumber, deviceName(), "Open", waitingNow, started);
}

/** Another device's survey status loads once per site. The site page and the Map's line fill in when it arrives. */
function loadStatusOnce(site: string): void {
  if (walkStatus.has(site)) return;
  walkStatus.set(site, null);
  void loadStatus(site);
}

function checkFor(site: SurveySite): { input: CheckInput; result: CheckResult } {
  const pack = packs.get(site.siteNumber) ?? null;
  const meta = packMeta(site.siteNumber);
  const input: CheckInput = {
    online: isOnline(),
    packSavedAt: pack && meta ? meta.savedAt : null,
    packCounts: pack && meta ? meta.counts : null,
    todayIso: new Date().toISOString(),
    homeScreenApp: homeScreenApp(),
    waiting: waitingNow,
    refused: refusedNow,
    otherDevice: otherDeviceFrom(walkStatus.get(site.siteNumber) ?? null, deviceId()),
    utility: pack?.assets.find(isUtility)?.assetId ?? null,
  };
  return { input, result: preCheck(input) };
}

function drawDownloading(site: SurveySite): void {
  if (!host) return;
  if (!deviceName()) nameOpen = true;
  host.frame(trail([{ label: "IEA Site Survey", href: "#/survey" }, { label: site.name }], false) + stripNow(), packDownloading(site.name) + overlays());
}

function drawSite(site: SurveySite): void {
  if (!host) return;
  const pack = packs.get(site.siteNumber) ?? null;
  if (!deviceName()) nameOpen = true;
  loadStatusOnce(site.siteNumber);
  host.frame(
    trail([{ label: "IEA Site Survey", href: "#/survey" }, { label: site.name }], Boolean(pack)) + stripNow(),
    sitePage({
      siteName: site.name,
      siteNumber: site.siteNumber,
      deviceName: deviceName() || "Not named",
      check: checkFor(site).result,
      online: isOnline(),
      pack,
      counts: pack ? surveyCounts(pack) : null,
      busy: packBusy,
      error: packError,
    }) + overlays(),
  );
}

function drawAsset(pack: SitePack, assetId: string): void {
  if (!host) return;
  const a = pack.assets.find((x) => x.assetId === assetId);
  const above =
    trail(
      [
        { label: "IEA Site Survey", href: "#/survey" },
        { label: pack.siteName, href: `#/survey/${pack.siteNumber}/walk` },
        { label: assetId.slice(3) },
      ],
      true,
    ) + stripNow();
  if (!a) {
    host.frame(above, scanResult("notInRegistry", assetId, "", "") + overlays());
    return;
  }
  host.frame(above, assetSummary(pack, a, tracedSet(pack).has(a.assetId)) + overlays());
}

function drawOutbox(pack: SitePack): void {
  if (!host) return;
  // Opened from the Asset ID Creator, the trail goes back to the Creator, not the survey (COHESION_WORDS.md Phase A).
  const fromCreator = parts()[0] === "assets";
  const above =
    trail(
      fromCreator
        ? [
            { label: "Assessment Sheet", href: "#/assets" },
            { label: pack.siteName, href: `#/assets/${pack.siteNumber}` },
            { label: "Outbox" },
          ]
        : [
            { label: "IEA Site Survey", href: "#/survey" },
            { label: pack.siteName, href: `#/survey/${pack.siteNumber}/walk` },
            { label: "Outbox" },
          ],
      false,
    ) + stripNow();
  if (!outboxView) {
    host.frame(above, loadingScreen("the Outbox"));
    void refreshOutbox();
    return;
  }
  const { records, photos, log } = outboxView;
  const counts = outboxCounts(records, photos, new Date().toISOString());
  const rows = records
    .filter((r) => r.state !== "sent")
    .sort((a, b) => a.savedAt.localeCompare(b.savedAt))
    .map((r) => ({ assetId: r.assetId, card: r.card, at: r.savedAt, photos: photos.filter((p) => p.recordPath === r.path && p.state !== "sent").length, state: r.state, reason: r.reason }))
    // Label PDFs waiting for Drive (SITE_FOLDERS_WORDS.md section 12), each on its own row.
    .concat(
      photos
        .filter((p) => isDrivePdf(p) && p.state !== "sent")
        .map((p) => ({ assetId: p.path.split("/").pop() ?? "", card: "Save to Drive", at: p.takenAt, photos: 0, state: p.state, reason: p.reason })),
    );
  host.frame(
    above,
    outboxScreen({
      online: isOnline(),
      counts,
      rows,
      photosHeld: photosHeld(),
      storage: storageNow,
      deviceName: deviceName(),
      log,
      busy: obBusy,
      flash: obFlash,
      error: obError,
    }) + overlays(),
  );
}

async function obRun(busy: string, job: () => Promise<string>): Promise<void> {
  if (obBusy) return;
  obBusy = busy;
  obFlash = "";
  obError = "";
  rerender();
  try {
    obFlash = await job();
  } catch (e) {
    obError = e instanceof Error ? e.message : String(e);
  }
  obBusy = "";
  await refreshOutbox();
}

function overlays(): string {
  return (scanOpen ? scanSheet(scanNote) : "") + (nameOpen ? nameSheet(deviceName() || "") : "");
}

// ---- Scan ----

function currentSite(): string {
  return parts()[1] ?? "";
}

function handleScanned(text: string): boolean {
  const site = currentSite();
  const pack = packs.get(site);
  if (!pack) return false;
  const r = resolveScan(text, location.host, site, new Set(pack.assets.map((a) => a.assetId)));
  if (r.kind === "open") {
    closeScan();
    if (parts()[2] === "map" && mapScanned(r.assetId)) {
      rerender();
      return true;
    }
    location.hash = `#/survey/${site}/a/${encodeURIComponent(r.assetId)}`;
    return true;
  }
  const other = r.kind === "otherSite" ? siteOf(r.siteNumber) : null;
  scanNote = scanResult(r.kind, "assetId" in r ? r.assetId : "", other?.name ?? (r.kind === "otherSite" ? `site ${r.siteNumber}` : ""), r.kind === "otherSite" ? r.siteNumber : "");
  const box = host?.root.querySelector(".sv-sheet");
  const at = box?.querySelector(".sv-result");
  if (at) at.outerHTML = scanNote;
  else box?.querySelector(".sv-type")?.insertAdjacentHTML("afterend", scanNote);
  return false;
}

function openScan(): void {
  scanOpen = true;
  scanNote = "";
  scanner?.stop();
  scanner = null;
  scanVideo = null;
  scanText = "";
  rerender();
}

/**
 * The scan sheet is open: make sure the camera runs in the sheet on screen (NAV_CRAWL_WORDS.md A7).
 * A repaint draws a fresh sheet. The running video moves into it in the same moment, so the camera never stops.
 * Called after every draw. Does nothing when the sheet is closed or the camera is already in place.
 */
function keepScanner(): void {
  if (!scanOpen || !host) return;
  const video = host.root.querySelector<HTMLVideoElement>("#sv-video");
  if (!video) return;
  const tx = host.root.querySelector<HTMLElement>("#sv-camtx");
  if (scanner && scanVideo) {
    if (video !== scanVideo) {
      video.replaceWith(scanVideo);
      void scanVideo.play().catch(() => undefined);
    }
    if (tx && scanText) tx.textContent = scanText;
    return;
  }
  scanVideo = video;
  scanner = startQrScanner(video, (link) => handleScanned(link), (t) => {
    scanText = t;
    const now = host?.root.querySelector<HTMLElement>("#sv-camtx");
    if (now) now.textContent = t;
  });
}

function closeScan(): void {
  scanner?.stop();
  scanner = null;
  scanVideo = null;
  scanText = "";
  scanOpen = false;
  scanNote = "";
}

// ---- Events ----

function wire(): void {
  if (wired || !host) return;
  wired = true;
  const root = host.root;
  root.addEventListener("click", (ev) => {
    if (!inSection()) return;
    const el = (ev.target as HTMLElement).closest<HTMLElement>("[data-sv]");
    if (!el) return;
    const act = el.dataset.sv;
    const site = siteOf(currentSite());
    if (act === "aa") {
      ev.stopPropagation();
      setItem(AA_KEY, isLarge() ? "standard" : "large");
      rerender();
    } else if (act === "sites-retry") retrySites();
    else if (act === "scan") openScan();
    else if (act === "scan-cancel") {
      closeScan();
      rerender();
    } else if (act === "pack" && site) void downloadPack(site);
    else if (act === "outbox" && site) location.hash = `#/${parts()[0] === "assets" ? "assets" : "survey"}/${site.siteNumber}/outbox`;
    else if (act === "upload") void obRun("upload", async () => {
      await sendWaiting(true);
      return "Upload All finished.";
    });
    else if (act === "photos-auto" || act === "photos-hold") {
      setPhotosHeld(act === "photos-hold");
      rerender();
    } else if (act === "export" && site && host) {
      const h = host;
      void obRun("export", () => exportBackup(h.badge.company, site.name, deviceName()));
    } else if (act === "end" && site && host) {
      const h = host;
      void obRun("end", async () => {
        markSurvey(h.badge, site.siteNumber, deviceName(), "Ended", 0, getItem(startedKey(site.siteNumber)) ?? "");
        store()?.removeItem(startedKey(site.siteNumber));
        await deviceList.addLog(`Survey ended at ${site.name}`);
        return "Survey ended. Other devices no longer see it.";
      });
    }
    else if (act === "name") {
      ev.preventDefault();
      nameOpen = true;
      rerender();
    }
    else if (act === "name-cancel" && deviceName()) {
      ev.preventDefault();
      nameOpen = false;
      rerender();
    }
  });
  root.addEventListener("change", (ev) => {
    if (!inSection()) return;
    const input = (ev.target as HTMLElement).closest<HTMLInputElement>("[data-sv-file]");
    const file = input?.files?.[0];
    if (!input || !file || !host) return;
    const h = host;
    input.value = "";
    void obRun("load", () => loadBackup(file, h.badge));
  });
  onOutboxChange(() => void refreshOutbox());
  window.addEventListener("online", () => void sendWaiting(false));
  void refreshOutbox();
  void sendWaiting(false);
  root.addEventListener("submit", (ev) => {
    if (!inSection()) return;
    const form = (ev.target as HTMLElement).closest<HTMLFormElement>("[data-sv-form]");
    if (!form) return;
    ev.preventDefault();
    if (form.dataset.svForm === "name") {
      const v = root.querySelector<HTMLInputElement>("#sv-name")?.value ?? "";
      setItem(DEVICE_KEY, cleanDeviceName(v));
      nameOpen = false;
      rerender();
    } else if (form.dataset.svForm === "type") {
      const v = root.querySelector<HTMLInputElement>("#sv-typeid")?.value ?? "";
      if (v.trim()) handleScanned(v);
    }
  });
  window.addEventListener("online", () => {
    // Signal is back: a sites read that failed tries again on its own (NAV_CRAWL_WORDS.md A4).
    if (sites === null && sitesError) sitesError = "";
    rerender();
  });
  window.addEventListener("offline", rerender);
  window.addEventListener("hashchange", () => {
    if (scanOpen) closeScan();
  });
}
